import * as i0 from '@angular/core';
import { forwardRef, EventEmitter, booleanAttribute, numberAttribute, Component, ChangeDetectionStrategy, ViewEncapsulation, Input, Output, ContentChild, ContentChildren, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { ObjectUtils } from 'primeng/utils';
import * as i1 from 'primeng/api';
import { TranslationKeys, Header, Footer, PrimeTemplate, SharedModule } from 'primeng/api';
import * as i3 from 'primeng/paginator';
import { PaginatorModule } from 'primeng/paginator';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { SpinnerIcon } from 'primeng/icons/spinner';
import { ThLargeIcon } from 'primeng/icons/thlarge';
import { BarsIcon } from 'primeng/icons/bars';

/**
 * DataView displays data in grid or list layout with pagination and sorting features.
 * @group Components
 */
const _c0 = [[["p-header"]], [["p-footer"]]];
const _c1 = ["p-header", "p-footer"];
const _c2 = (a0, a1) => ({
  "p-dataview p-component": true,
  "p-dataview-list": a0,
  "p-dataview-grid": a1
});
const _c3 = a0 => ({
  "p-disabled": a0
});
const _c4 = a0 => ({
  $implicit: a0
});
function DataView_div_1_i_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "i");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵclassMap("p-dataview-loading-icon pi-spin " + ctx_r0.loadingIcon);
  }
}
function DataView_div_1_ng_container_3_SpinnerIcon_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "SpinnerIcon", 15);
  }
  if (rf & 2) {
    i0.ɵɵproperty("spin", true)("styleClass", "p-dataview-loading-icon");
  }
}
function DataView_div_1_ng_container_3_2_ng_template_0_Template(rf, ctx) {}
function DataView_div_1_ng_container_3_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DataView_div_1_ng_container_3_2_ng_template_0_Template, 0, 0, "ng-template");
  }
}
function DataView_div_1_ng_container_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, DataView_div_1_ng_container_3_SpinnerIcon_1_Template, 1, 2, "SpinnerIcon", 13)(2, DataView_div_1_ng_container_3_2_Template, 1, 0, null, 14);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r0.loadingIconTemplate);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.loadingIconTemplate);
  }
}
function DataView_div_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 10)(1, "div", 11);
    i0.ɵɵtemplate(2, DataView_div_1_i_2_Template, 1, 2, "i", 12)(3, DataView_div_1_ng_container_3_Template, 3, 2, "ng-container", 7);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r0.loadingIcon);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r0.loadingIcon);
  }
}
function DataView_div_2_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function DataView_div_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 16);
    i0.ɵɵprojection(1);
    i0.ɵɵtemplate(2, DataView_div_2_ng_container_2_Template, 1, 0, "ng-container", 14);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.headerTemplate);
  }
}
function DataView_p_paginator_3_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "p-paginator", 17);
    i0.ɵɵlistener("onPageChange", function DataView_p_paginator_3_Template_p_paginator_onPageChange_0_listener($event) {
      i0.ɵɵrestoreView(_r2);
      const ctx_r0 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r0.paginate($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("rows", ctx_r0.rows)("first", ctx_r0.first)("totalRecords", ctx_r0.totalRecords)("pageLinkSize", ctx_r0.pageLinks)("alwaysShow", ctx_r0.alwaysShowPaginator)("rowsPerPageOptions", ctx_r0.rowsPerPageOptions)("dropdownAppendTo", ctx_r0.paginatorDropdownAppendTo)("dropdownScrollHeight", ctx_r0.paginatorDropdownScrollHeight)("templateLeft", ctx_r0.paginatorLeftTemplate)("templateRight", ctx_r0.paginatorRightTemplate)("currentPageReportTemplate", ctx_r0.currentPageReportTemplate)("showFirstLastIcon", ctx_r0.showFirstLastIcon)("dropdownItemTemplate", ctx_r0.paginatorDropdownItemTemplate)("showCurrentPageReport", ctx_r0.showCurrentPageReport)("showJumpToPageDropdown", ctx_r0.showJumpToPageDropdown)("showPageLinks", ctx_r0.showPageLinks)("styleClass", ctx_r0.paginatorStyleClass);
  }
}
function DataView_ng_container_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function DataView_div_7_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r0.emptyMessageLabel, " ");
  }
}
function DataView_div_7_ng_container_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0, null, 0);
  }
}
function DataView_div_7_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div")(1, "div", 18);
    i0.ɵɵtemplate(2, DataView_div_7_ng_container_2_Template, 2, 1, "ng-container", 19)(3, DataView_div_7_ng_container_3_Template, 2, 0, "ng-container", 14);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !ctx_r0.emptyMessageTemplate)("ngIfElse", ctx_r0.empty);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.emptyMessageTemplate);
  }
}
function DataView_p_paginator_8_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "p-paginator", 20);
    i0.ɵɵlistener("onPageChange", function DataView_p_paginator_8_Template_p_paginator_onPageChange_0_listener($event) {
      i0.ɵɵrestoreView(_r3);
      const ctx_r0 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r0.paginate($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("rows", ctx_r0.rows)("first", ctx_r0.first)("totalRecords", ctx_r0.totalRecords)("pageLinkSize", ctx_r0.pageLinks)("alwaysShow", ctx_r0.alwaysShowPaginator)("rowsPerPageOptions", ctx_r0.rowsPerPageOptions)("dropdownAppendTo", ctx_r0.paginatorDropdownAppendTo)("dropdownScrollHeight", ctx_r0.paginatorDropdownScrollHeight)("templateLeft", ctx_r0.paginatorLeftTemplate)("templateRight", ctx_r0.paginatorRightTemplate)("currentPageReportTemplate", ctx_r0.currentPageReportTemplate)("showFirstLastIcon", ctx_r0.showFirstLastIcon)("dropdownItemTemplate", ctx_r0.paginatorDropdownItemTemplate)("showCurrentPageReport", ctx_r0.showCurrentPageReport)("showJumpToPageDropdown", ctx_r0.showJumpToPageDropdown)("showPageLinks", ctx_r0.showPageLinks)("styleClass", ctx_r0.paginatorStyleClass);
  }
}
function DataView_div_9_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function DataView_div_9_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 21);
    i0.ɵɵprojection(1, 1);
    i0.ɵɵtemplate(2, DataView_div_9_ng_container_2_Template, 1, 0, "ng-container", 14);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.footerTemplate);
  }
}
const _c5 = a0 => ({
  "p-highlight": a0
});
function DataViewLayoutOptions_BarsIcon_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "BarsIcon");
  }
}
function DataViewLayoutOptions_3_ng_template_0_Template(rf, ctx) {}
function DataViewLayoutOptions_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DataViewLayoutOptions_3_ng_template_0_Template, 0, 0, "ng-template");
  }
}
function DataViewLayoutOptions_ThLargeIcon_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "ThLargeIcon");
  }
}
function DataViewLayoutOptions_6_ng_template_0_Template(rf, ctx) {}
function DataViewLayoutOptions_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DataViewLayoutOptions_6_ng_template_0_Template, 0, 0, "ng-template");
  }
}
const DATAVIEW_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DataView),
  multi: true
};
let DataView = /*#__PURE__*/(() => {
  class DataView {
    el;
    cd;
    filterService;
    config;
    /**
     * When specified as true, enables the pagination.
     * @group Props
     */
    paginator;
    /**
     * Number of rows to display per page.
     * @group Props
     */
    rows;
    /**
     * Number of total records, defaults to length of value when not defined.
     * @group Props
     */
    totalRecords;
    /**
     * Number of page links to display in paginator.
     * @group Props
     */
    pageLinks = 5;
    /**
     * Array of integer/object values to display inside rows per page dropdown of paginator
     * @group Props
     */
    rowsPerPageOptions;
    /**
     * Position of the paginator.
     * @group Props
     */
    paginatorPosition = 'bottom';
    /**
     * Custom style class for paginator
     * @group Props
     */
    paginatorStyleClass;
    /**
     * Whether to show it even there is only one page.
     * @group Props
     */
    alwaysShowPaginator = true;
    /**
     * Target element to attach the paginator dropdown overlay, valid values are "body" or a local ng-template variable of another element (note: use binding with brackets for template variables, e.g. [appendTo]="mydiv" for a div element having #mydiv as variable name).
     * @group Props
     */
    paginatorDropdownAppendTo;
    /**
     * Paginator dropdown height of the viewport in pixels, a scrollbar is defined if height of list exceeds this value.
     * @group Props
     */
    paginatorDropdownScrollHeight = '200px';
    /**
     * Template of the current page report element. Available placeholders are {currentPage},{totalPages},{rows},{first},{last} and {totalRecords}
     * @group Props
     */
    currentPageReportTemplate = '{currentPage} of {totalPages}';
    /**
     * Whether to display current page report.
     * @group Props
     */
    showCurrentPageReport;
    /**
     * Whether to display a dropdown to navigate to any page.
     * @group Props
     */
    showJumpToPageDropdown;
    /**
     * When enabled, icons are displayed on paginator to go first and last page.
     * @group Props
     */
    showFirstLastIcon = true;
    /**
     * Whether to show page links.
     * @group Props
     */
    showPageLinks = true;
    /**
     * Defines if data is loaded and interacted with in lazy manner.
     * @group Props
     */
    lazy;
    /**
     * Whether to call lazy loading on initialization.
     * @group Props
     */
    lazyLoadOnInit = true;
    /**
     * Text to display when there is no data. Defaults to global value in i18n translation configuration.
     * @group Props
     */
    emptyMessage = '';
    /**
     * Inline style of the component.
     * @group Props
     */
    style;
    /**
     * Style class of the component.
     * @group Props
     */
    styleClass;
    /**
     * Style class of the grid.
     * @group Props
     */
    gridStyleClass = '';
    /**
     * Function to optimize the dom operations by delegating to ngForTrackBy, default algorithm checks for object identity.
     * @group Props
     */
    trackBy = (index, item) => item;
    /**
     * Comma separated list of fields in the object graph to search against.
     * @group Props
     */
    filterBy;
    /**
     * Locale to use in filtering. The default locale is the host environment's current locale.
     * @group Props
     */
    filterLocale;
    /**
     * Displays a loader to indicate data load is in progress.
     * @group Props
     */
    loading;
    /**
     * The icon to show while indicating data load is in progress.
     * @group Props
     */
    loadingIcon;
    /**
     * Index of the first row to be displayed.
     * @group Props
     */
    first = 0;
    /**
     * Property name of data to use in sorting by default.
     * @group Props
     */
    sortField;
    /**
     * Order to sort the data by default.
     * @group Props
     */
    sortOrder;
    /**
     * An array of objects to display.
     * @group Props
     */
    value;
    /**
     * Defines the layout mode.
     * @group Props
     */
    get layout() {
      return this._layout;
    }
    set layout(layout) {
      this._layout = layout;
      if (this.initialized) {
        this.changeLayout(layout);
      }
    }
    /**
     * Callback to invoke when paging, sorting or filtering happens in lazy mode.
     * @param {DataViewLazyLoadEvent} event - Custom lazy load event.
     * @group Emits
     */
    onLazyLoad = new EventEmitter();
    /**
     * Callback to invoke when pagination occurs.
     * @param {DataViewPageEvent} event - Custom page event.
     * @group Emits
     */
    onPage = new EventEmitter();
    /**
     * Callback to invoke when sorting occurs.
     * @param {DataViewSortEvent} event - Custom sort event.
     * @group Emits
     */
    onSort = new EventEmitter();
    /**
     * Callback to invoke when changing layout.
     * @param {DataViewLayoutChangeEvent} event - Custom layout change event.
     * @group Emits
     */
    onChangeLayout = new EventEmitter();
    header;
    footer;
    templates;
    _value;
    listTemplate;
    gridTemplate;
    itemTemplate;
    disabled;
    headerTemplate;
    emptyMessageTemplate;
    footerTemplate;
    paginatorLeftTemplate;
    paginatorRightTemplate;
    paginatorDropdownItemTemplate;
    loadingIconTemplate;
    listIconTemplate;
    gridIconTemplate;
    filteredValue;
    filterValue;
    initialized;
    _layout = 'list';
    translationSubscription;
    get emptyMessageLabel() {
      return this.emptyMessage || this.config.getTranslation(TranslationKeys.EMPTY_MESSAGE);
    }
    onModelChange = () => {};
    onModelTouched = () => {};
    constructor(el, cd, filterService, config) {
      this.el = el;
      this.cd = cd;
      this.filterService = filterService;
      this.config = config;
    }
    ngOnInit() {
      if (this.lazy && this.lazyLoadOnInit) {
        this.onLazyLoad.emit(this.createLazyLoadMetadata());
      }
      this.translationSubscription = this.config.translationObserver.subscribe(() => {
        this.cd.markForCheck();
      });
      this.initialized = true;
    }
    ngOnChanges(simpleChanges) {
      if (simpleChanges.value) {
        this._value = simpleChanges.value.currentValue;
        this.updateTotalRecords();
        if (!this.lazy && this.hasFilter()) {
          this.filter(this.filterValue);
        }
      }
      if (simpleChanges.sortField || simpleChanges.sortOrder) {
        //avoid triggering lazy load prior to lazy initialization at onInit
        if (!this.lazy || this.initialized) {
          this.sort();
        }
      }
    }
    ngAfterContentInit() {
      this.templates.forEach(item => {
        switch (item.getType()) {
          case 'listItem':
          case 'list':
            this.listTemplate = item.template;
            break;
          case 'gridItem':
          case 'grid':
            this.gridTemplate = item.template;
            break;
          case 'paginatorleft':
            this.paginatorLeftTemplate = item.template;
            break;
          case 'paginatorright':
            this.paginatorRightTemplate = item.template;
            break;
          case 'paginatordropdownitem':
            this.paginatorDropdownItemTemplate = item.template;
            break;
          case 'empty':
            this.emptyMessageTemplate = item.template;
            break;
          case 'header':
            this.headerTemplate = item.template;
            break;
          case 'footer':
            this.footerTemplate = item.template;
            break;
          case 'loadingicon':
            this.loadingIconTemplate = item.template;
            break;
          case 'listicon':
            this.listIconTemplate = item.template;
            break;
          case 'gridicon':
            this.gridIconTemplate = item.template;
            break;
        }
      });
      this.updateItemTemplate();
    }
    writeValue(value) {}
    registerOnChange(fn) {
      this.onModelChange = fn;
    }
    registerOnTouched(fn) {
      this.onModelTouched = fn;
    }
    setDisabledState(val) {
      this.disabled = val;
      this.cd.markForCheck();
    }
    updateItemTemplate() {
      switch (this.layout) {
        case 'list':
          this.itemTemplate = this.listTemplate;
          break;
        case 'grid':
          this.itemTemplate = this.gridTemplate;
          break;
      }
    }
    changeLayout(layout) {
      this._layout = layout;
      this.onChangeLayout.emit({
        layout: this.layout
      });
      this.updateItemTemplate();
      this.cd.markForCheck();
    }
    updateTotalRecords() {
      this.totalRecords = this.lazy ? this.totalRecords : this._value ? this._value.length : 0;
    }
    paginate(event) {
      this.first = event.first;
      this.rows = event.rows;
      if (this.lazy) {
        this.onLazyLoad.emit(this.createLazyLoadMetadata());
      }
      this.onPage.emit({
        first: this.first,
        rows: this.rows
      });
    }
    sort() {
      this.first = 0;
      if (this.lazy) {
        this.onLazyLoad.emit(this.createLazyLoadMetadata());
      } else if (this.value) {
        this.value.sort((data1, data2) => {
          let value1 = ObjectUtils.resolveFieldData(data1, this.sortField);
          let value2 = ObjectUtils.resolveFieldData(data2, this.sortField);
          let result = null;
          if (value1 == null && value2 != null) result = -1;else if (value1 != null && value2 == null) result = 1;else if (value1 == null && value2 == null) result = 0;else if (typeof value1 === 'string' && typeof value2 === 'string') result = value1.localeCompare(value2);else result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
          return this.sortOrder * result;
        });
        if (this.hasFilter()) {
          this.filter(this.filterValue);
        }
      }
      this.onSort.emit({
        sortField: this.sortField,
        sortOrder: this.sortOrder
      });
    }
    isEmpty() {
      let data = this.filteredValue || this.value;
      return data == null || data.length == 0;
    }
    createLazyLoadMetadata() {
      return {
        first: this.first,
        rows: this.rows,
        sortField: this.sortField,
        sortOrder: this.sortOrder
      };
    }
    getBlockableElement() {
      return this.el.nativeElement.children[0];
    }
    filter(filter, filterMatchMode = 'contains') {
      this.filterValue = filter;
      if (this.value && this.value.length) {
        let searchFields = this.filterBy.split(',');
        this.filteredValue = this.filterService.filter(this.value, searchFields, filter, filterMatchMode, this.filterLocale);
        if (this.filteredValue.length === this.value.length) {
          this.filteredValue = null;
        }
        if (this.paginator) {
          this.first = 0;
          this.totalRecords = this.filteredValue ? this.filteredValue.length : this.value ? this.value.length : 0;
        }
        this.cd.markForCheck();
      }
    }
    hasFilter() {
      return this.filterValue && this.filterValue.trim().length > 0;
    }
    ngOnDestroy() {
      if (this.translationSubscription) {
        this.translationSubscription.unsubscribe();
      }
    }
    static ɵfac = function DataView_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DataView)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.FilterService), i0.ɵɵdirectiveInject(i1.PrimeNGConfig));
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: DataView,
      selectors: [["p-dataView"]],
      contentQueries: function DataView_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, Header, 5);
          i0.ɵɵcontentQuery(dirIndex, Footer, 5);
          i0.ɵɵcontentQuery(dirIndex, PrimeTemplate, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.header = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.footer = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.templates = _t);
        }
      },
      hostAttrs: [1, "p-element"],
      inputs: {
        paginator: [2, "paginator", "paginator", booleanAttribute],
        rows: [2, "rows", "rows", numberAttribute],
        totalRecords: [2, "totalRecords", "totalRecords", numberAttribute],
        pageLinks: [2, "pageLinks", "pageLinks", numberAttribute],
        rowsPerPageOptions: "rowsPerPageOptions",
        paginatorPosition: "paginatorPosition",
        paginatorStyleClass: "paginatorStyleClass",
        alwaysShowPaginator: [2, "alwaysShowPaginator", "alwaysShowPaginator", booleanAttribute],
        paginatorDropdownAppendTo: "paginatorDropdownAppendTo",
        paginatorDropdownScrollHeight: "paginatorDropdownScrollHeight",
        currentPageReportTemplate: "currentPageReportTemplate",
        showCurrentPageReport: [2, "showCurrentPageReport", "showCurrentPageReport", booleanAttribute],
        showJumpToPageDropdown: [2, "showJumpToPageDropdown", "showJumpToPageDropdown", booleanAttribute],
        showFirstLastIcon: [2, "showFirstLastIcon", "showFirstLastIcon", booleanAttribute],
        showPageLinks: [2, "showPageLinks", "showPageLinks", booleanAttribute],
        lazy: [2, "lazy", "lazy", booleanAttribute],
        lazyLoadOnInit: [2, "lazyLoadOnInit", "lazyLoadOnInit", booleanAttribute],
        emptyMessage: "emptyMessage",
        style: "style",
        styleClass: "styleClass",
        gridStyleClass: "gridStyleClass",
        trackBy: "trackBy",
        filterBy: "filterBy",
        filterLocale: "filterLocale",
        loading: [2, "loading", "loading", booleanAttribute],
        loadingIcon: "loadingIcon",
        first: [2, "first", "first", numberAttribute],
        sortField: "sortField",
        sortOrder: [2, "sortOrder", "sortOrder", numberAttribute],
        value: "value",
        layout: "layout",
        disabled: "disabled"
      },
      outputs: {
        onLazyLoad: "onLazyLoad",
        onPage: "onPage",
        onSort: "onSort",
        onChangeLayout: "onChangeLayout"
      },
      standalone: false,
      features: [i0.ɵɵProvidersFeature([DATAVIEW_VALUE_ACCESSOR]), i0.ɵɵInputTransformsFeature, i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c1,
      decls: 10,
      vars: 25,
      consts: [["empty", ""], [3, "ngClass", "ngStyle"], ["class", "p-dataview-loading", 4, "ngIf"], ["class", "p-dataview-header", 4, "ngIf"], ["styleClass", "p-paginator-top", 3, "rows", "first", "totalRecords", "pageLinkSize", "alwaysShow", "rowsPerPageOptions", "dropdownAppendTo", "dropdownScrollHeight", "templateLeft", "templateRight", "currentPageReportTemplate", "showFirstLastIcon", "dropdownItemTemplate", "showCurrentPageReport", "showJumpToPageDropdown", "showPageLinks", "styleClass", "onPageChange", 4, "ngIf"], [1, "p-dataview-content", 3, "ngClass"], [4, "ngTemplateOutlet", "ngTemplateOutletContext"], [4, "ngIf"], ["styleClass", "p-paginator-bottom", 3, "rows", "first", "totalRecords", "pageLinkSize", "alwaysShow", "rowsPerPageOptions", "dropdownAppendTo", "dropdownScrollHeight", "templateLeft", "templateRight", "currentPageReportTemplate", "showFirstLastIcon", "dropdownItemTemplate", "showCurrentPageReport", "showJumpToPageDropdown", "showPageLinks", "styleClass", "onPageChange", 4, "ngIf"], ["class", "p-dataview-footer", 4, "ngIf"], [1, "p-dataview-loading"], [1, "p-dataview-loading-overlay", "p-component-overlay"], [3, "class", 4, "ngIf"], [3, "spin", "styleClass", 4, "ngIf"], [4, "ngTemplateOutlet"], [3, "spin", "styleClass"], [1, "p-dataview-header"], ["styleClass", "p-paginator-top", 3, "onPageChange", "rows", "first", "totalRecords", "pageLinkSize", "alwaysShow", "rowsPerPageOptions", "dropdownAppendTo", "dropdownScrollHeight", "templateLeft", "templateRight", "currentPageReportTemplate", "showFirstLastIcon", "dropdownItemTemplate", "showCurrentPageReport", "showJumpToPageDropdown", "showPageLinks", "styleClass"], [1, "p-dataview-emptymessage"], [4, "ngIf", "ngIfElse"], ["styleClass", "p-paginator-bottom", 3, "onPageChange", "rows", "first", "totalRecords", "pageLinkSize", "alwaysShow", "rowsPerPageOptions", "dropdownAppendTo", "dropdownScrollHeight", "templateLeft", "templateRight", "currentPageReportTemplate", "showFirstLastIcon", "dropdownItemTemplate", "showCurrentPageReport", "showJumpToPageDropdown", "showPageLinks", "styleClass"], [1, "p-dataview-footer"]],
      template: function DataView_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c0);
          i0.ɵɵelementStart(0, "div", 1);
          i0.ɵɵtemplate(1, DataView_div_1_Template, 4, 2, "div", 2)(2, DataView_div_2_Template, 3, 1, "div", 3)(3, DataView_p_paginator_3_Template, 1, 17, "p-paginator", 4);
          i0.ɵɵelementStart(4, "div", 5);
          i0.ɵɵtemplate(5, DataView_ng_container_5_Template, 1, 0, "ng-container", 6);
          i0.ɵɵpipe(6, "slice");
          i0.ɵɵtemplate(7, DataView_div_7_Template, 4, 3, "div", 7);
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(8, DataView_p_paginator_8_Template, 1, 17, "p-paginator", 8)(9, DataView_div_9_Template, 3, 1, "div", 9);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵclassMap(ctx.styleClass);
          i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(18, _c2, ctx.layout === "list", ctx.layout === "grid"))("ngStyle", ctx.style);
          i0.ɵɵattribute("disabled", ctx.disabled);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", ctx.loading);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", ctx.header || ctx.headerTemplate);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", ctx.paginator && (ctx.paginatorPosition === "top" || ctx.paginatorPosition == "both"));
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(21, _c3, ctx.disabled));
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngTemplateOutlet", ctx.itemTemplate)("ngTemplateOutletContext", i0.ɵɵpureFunction1(23, _c4, ctx.paginator ? i0.ɵɵpipeBind3(6, 14, ctx.filteredValue || ctx.value, ctx.lazy ? 0 : ctx.first, (ctx.lazy ? 0 : ctx.first) + ctx.rows) : ctx.filteredValue || ctx.value));
          i0.ɵɵadvance(2);
          i0.ɵɵproperty("ngIf", ctx.isEmpty() && !ctx.loading);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", ctx.paginator && (ctx.paginatorPosition === "bottom" || ctx.paginatorPosition == "both"));
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", ctx.footer || ctx.footerTemplate);
        }
      },
      dependencies: () => [i2.NgClass, i2.NgIf, i2.NgTemplateOutlet, i2.NgStyle, i3.Paginator, SpinnerIcon, i2.SlicePipe],
      styles: [".p-dataview{position:relative}.p-dataview .p-dataview-loading-overlay{position:absolute;display:flex;align-items:center;justify-content:center;z-index:2}\n"],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return DataView;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DataViewLayoutOptions = /*#__PURE__*/(() => {
  class DataViewLayoutOptions {
    dv;
    style;
    styleClass;
    constructor(dv) {
      this.dv = dv;
    }
    changeLayout(event, layout) {
      this.dv.changeLayout(layout);
      event.preventDefault();
    }
    static ɵfac = function DataViewLayoutOptions_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DataViewLayoutOptions)(i0.ɵɵdirectiveInject(DataView));
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: DataViewLayoutOptions,
      selectors: [["p-dataViewLayoutOptions"]],
      hostAttrs: [1, "p-element"],
      inputs: {
        style: "style",
        styleClass: "styleClass"
      },
      standalone: false,
      decls: 7,
      vars: 14,
      consts: [[3, "ngClass", "ngStyle"], ["type", "button", 1, "p-button", "p-button-icon-only", 3, "click", "keydown.enter", "ngClass"], [4, "ngIf"], [4, "ngTemplateOutlet"]],
      template: function DataViewLayoutOptions_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "div", 0)(1, "button", 1);
          i0.ɵɵlistener("click", function DataViewLayoutOptions_Template_button_click_1_listener($event) {
            return ctx.changeLayout($event, "list");
          })("keydown.enter", function DataViewLayoutOptions_Template_button_keydown_enter_1_listener($event) {
            return ctx.changeLayout($event, "list");
          });
          i0.ɵɵtemplate(2, DataViewLayoutOptions_BarsIcon_2_Template, 1, 0, "BarsIcon", 2)(3, DataViewLayoutOptions_3_Template, 1, 0, null, 3);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(4, "button", 1);
          i0.ɵɵlistener("click", function DataViewLayoutOptions_Template_button_click_4_listener($event) {
            return ctx.changeLayout($event, "grid");
          })("keydown.enter", function DataViewLayoutOptions_Template_button_keydown_enter_4_listener($event) {
            return ctx.changeLayout($event, "grid");
          });
          i0.ɵɵtemplate(5, DataViewLayoutOptions_ThLargeIcon_5_Template, 1, 0, "ThLargeIcon", 2)(6, DataViewLayoutOptions_6_Template, 1, 0, null, 3);
          i0.ɵɵelementEnd()();
        }
        if (rf & 2) {
          i0.ɵɵclassMap(ctx.styleClass);
          i0.ɵɵproperty("ngClass", "p-dataview-layout-options p-selectbutton p-buttonset")("ngStyle", ctx.style);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(10, _c5, ctx.dv.layout === "list"));
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", !ctx.dv.listIconTemplate);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngTemplateOutlet", ctx.dv.listIconTemplate);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(12, _c5, ctx.dv.layout === "grid"));
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngIf", !ctx.dv.gridIconTemplate);
          i0.ɵɵadvance();
          i0.ɵɵproperty("ngTemplateOutlet", ctx.dv.gridIconTemplate);
        }
      },
      dependencies: () => [i2.NgClass, i2.NgIf, i2.NgTemplateOutlet, i2.NgStyle, BarsIcon, ThLargeIcon],
      encapsulation: 2
    });
  }
  return DataViewLayoutOptions;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DataViewModule = /*#__PURE__*/(() => {
  class DataViewModule {
    static ɵfac = function DataViewModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DataViewModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: DataViewModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, SharedModule, PaginatorModule, SpinnerIcon, BarsIcon, ThLargeIcon, SharedModule]
    });
  }
  return DataViewModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DATAVIEW_VALUE_ACCESSOR, DataView, DataViewLayoutOptions, DataViewModule };
